import { useState, useEffect } from 'react';
import { SubscriptionUser, UserLicenseModel, UserRole } from 'store/account/subscription';

import { SubscriptionUsersList } from './List';
import {
	AddSubscriptionUserModal,
	DeactivateEnterpriseUser,
	EditSubscriptionUserModal,
	EditSubscriptionUserTab,
	ResetSubscriptionUserPasswordModal
} from './Modals';
import { AppendToLicenseContainer } from './SubscriptionUser.style';
import { Switch } from 'components/UI/Interactables/Switch';
import { Button } from 'components/UI/Interactables/Button';
import { Modal } from 'components/UI/Modal';
import { Spacer } from 'components/UI/Spacer';
import { Typography } from 'components/UI/Typography';
import {
	useTranslation,
	useChangeUserLicenseModel,
	useSubscriptionCancelUserInvitation,
	useSubscription,
	useSubscriptionBundleFlag,
	useLicenseModels
} from 'hooks/store';
import { ReactivateEnterpriseUser } from './Modals/ReactivateEnterpriseUser';

export interface CancelUserInvitePayload {
	id: string;
	email: string;
	licenseModel: UserLicenseModel;
}

interface Props {
	showAddUserModal: boolean;
	setShowAddUserModal: (showAddUserModal: boolean) => void;
}

export function SubscriptionUsers({ showAddUserModal, setShowAddUserModal }: Props) {
	const { translate } = useTranslation();

	const { subscriptionBundleFlag } = useSubscriptionBundleFlag();

	const [{ loading: changeLicenseLoading }, changeLicenseType] = useChangeUserLicenseModel();
	const [
		{ loading: cancellingUserInvitation, error: errorCancellingUserInvitation },
		cancelUserSubscriptionInvitation
	] = useSubscriptionCancelUserInvitation();
	const [
		{
			data: {
				users,
				details,
				subscriptionTypes: {
					isNonSubscriber,
					isLedidiFree,
					isLedidiFreeTrial,
					isLedidiCollaborator,
					isLedidiCore,
					isLedidiEnterprise
				},
				userTypesAndRoles: { ledidiEnterprise, isPaidSubscriptionOwner }
			}
		}
	] = useSubscription();

	const [
		{
			data: { availableLicensesCount }
		}
	] = useLicenseModels();

	const [editUserModalProps, setEditUserModalProps] = useState<null | {
		user: SubscriptionUser;
		selectedTab?: EditSubscriptionUserTab;
	}>(null);
	const [resetUserPassword, setResetUserPassword] = useState<string | null>(null);
	const [deactivateEnterpriseUserId, setDeactivateEnterpriseUserId] = useState<string | null>(
		null
	);
	const [reactivateEnterpriseUserId, setReactivateEnterpriseUserId] = useState<string | null>(
		null
	);

	const [cancelInvitationForUser, setCancelInvitationForUser] =
		useState<CancelUserInvitePayload | null>(null);

	const [ownerAssignedToLicense, setOwnerAssignedToLicense] = useState(
		users.find(user => user.isOwner)?.licenseModel === UserLicenseModel.Full
	);

	useEffect(() => {
		if (!errorCancellingUserInvitation && !cancellingUserInvitation)
			setCancelInvitationForUser(null);
	}, [cancellingUserInvitation]);

	useEffect(() => {
		if (details) {
			const ownerHasFullLicense =
				users.find(user => user.isOwner)?.licenseModel === UserLicenseModel.Full;

			if (ownerHasFullLicense && availableLicensesCount > 0)
				setOwnerAssignedToLicense(ownerHasFullLicense);
		}
	}, [users]);

	function onEditUserClicked(user: SubscriptionUser, selectedTab?: EditSubscriptionUserTab) {
		setEditUserModalProps({ user, selectedTab });
	}

	function onCancelUserInvitation(payload: CancelUserInvitePayload) {
		setCancelInvitationForUser(payload);
	}

	function onConfirmCancelUserInvitation() {
		if (!cancelInvitationForUser) return;
		cancelUserSubscriptionInvitation(
			cancelInvitationForUser.id,
			cancelInvitationForUser.licenseModel
		);
	}

	function handleOwnerToLicense() {
		const ownerUserId = users.find(user => user.isOwner)?.userid;
		if (!ownerUserId) return;

		setOwnerAssignedToLicense(!ownerAssignedToLicense);
		changeLicenseType({
			licenseModel:
				users.find(user => user.isOwner)?.licenseModel === UserLicenseModel.Full
					? UserLicenseModel.OneOwnedOneShared
					: UserLicenseModel.Full,
			userId: ownerUserId
		});
	}

	function onAddUserClicked() {
		setShowAddUserModal(true);
	}

	function allowEditEnterpriseUser(userRole: UserRole) {
		if (ledidiEnterprise.isOrganizationAdmin) return true;

		if (
			ledidiEnterprise.isSuperAdmin &&
			(userRole === UserRole.Admin || userRole === UserRole.User)
		)
			return true;

		if (ledidiEnterprise.isAdmin && userRole === UserRole.User) return true;

		return false;
	}

	if (isLedidiFree || isLedidiFreeTrial || isNonSubscriber) return <></>;

	const canAddUser = availableLicensesCount > 0 && !isLedidiCollaborator;
	const assignOneLicenseEnabled =
		(availableLicensesCount > 0 &&
			users.find(user => user.isOwner)?.licenseModel ===
				UserLicenseModel.OneOwnedOneShared) ||
		users.find(user => user.isOwner)?.licenseModel === UserLicenseModel.Full;
	return (
		<>
			<AppendToLicenseContainer alignLeft={isLedidiEnterprise} alignRight={canAddUser}>
				{canAddUser && !isLedidiEnterprise && (
					<Button
						title={translate(({ accountUM }) => accountUM.subscription.addNewUser)}
						onClick={onAddUserClicked}
						variant={v => v.outline}
						minWidth={20}
					/>
				)}
				<>
					{isLedidiCore && !subscriptionBundleFlag && (
						<Switch
							label={translate(
								({ accountUM }) => accountUM.subscription.assignOneLicenseToYourself
							)}
							disabled={changeLicenseLoading || !assignOneLicenseEnabled}
							on={
								users.find(user => user.isOwner)?.licenseModel ===
								UserLicenseModel.Full
							}
							onChange={handleOwnerToLicense}
						/>
					)}
				</>
				{/* {hasAvailableLicenseQuantity && (
					<Button
						title={'Import users'}
						variant={v => v.secondary}
						marginOffset={hasAvailableLicenseQuantity && { left: 2.2 }}
						minWidth={20}
					/>
				)} */}
			</AppendToLicenseContainer>

			<Spacer size={s => s.s} />
			{
				<SubscriptionUsersList
					isOwner={isPaidSubscriptionOwner}
					isLedidiEnterprise={isLedidiEnterprise}
					users={users}
					ownerAssignedToLicense={
						!!users.find(
							user =>
								user.isOwner &&
								(user.licenseModel === UserLicenseModel.Full ||
									user.licenseModel === UserLicenseModel.Collaborator)
						)
					}
					allowEditEnterpriseUser={allowEditEnterpriseUser}
					onEditUser={onEditUserClicked}
					onCancelUserInvitation={onCancelUserInvitation}
					onResetPassword={userId => setResetUserPassword(userId)}
					onDeactivateEnterpriseUser={userId => setDeactivateEnterpriseUserId(userId)}
					onReactivateEnterpriseUser={userId => setReactivateEnterpriseUserId(userId)}
				/>
			}
			{cancelInvitationForUser && (
				<Modal
					title={translate(
						({ accountUM }) => accountUM.subscription.removeUserInvitation
					)}
					visible={!!cancelInvitationForUser}
					onClose={() => setCancelInvitationForUser(null)}
					neutral={{
						label: translate(({ buttons }) => buttons.cancel),
						disabled: cancellingUserInvitation,
						onClick: () => setCancelInvitationForUser(null)
					}}
					size={s => s.s}
					primary={{
						onClick: onConfirmCancelUserInvitation,
						label: translate(({ buttons }) => buttons.confirm),
						loading: cancellingUserInvitation,
						disabled: cancellingUserInvitation
					}}
					closeOnBackdrop
					enterAsPrimaryOnClick
				>
					<Typography.Paragraph>
						{translate(
							({ accountUM }) => accountUM.subscription.cancelUserInvitationWarning
						)}
						&nbsp;
						<b>{cancelInvitationForUser.email}</b>?
					</Typography.Paragraph>
				</Modal>
			)}

			{/* ADD USER MODAL */}
			{showAddUserModal && (
				<AddSubscriptionUserModal
					visible={showAddUserModal}
					onClose={() => setShowAddUserModal(false)}
				/>
			)}

			{/* EDIT MODAL */}
			{editUserModalProps && (
				<EditSubscriptionUserModal
					user={editUserModalProps.user}
					selectedTab={editUserModalProps.selectedTab}
					onClose={() => setEditUserModalProps(null)}
				/>
			)}

			{/* RESET PASSWORD */}
			{resetUserPassword && (
				<ResetSubscriptionUserPasswordModal
					onClose={() => setResetUserPassword(null)}
					userId={resetUserPassword}
				/>
			)}

			{/* DEACTIVATE ENTERPRISE USER */}
			{deactivateEnterpriseUserId && (
				<DeactivateEnterpriseUser
					onClose={() => setDeactivateEnterpriseUserId(null)}
					userId={deactivateEnterpriseUserId}
				/>
			)}

			{/* REACTIVATE ENTERPRISE USER */}
			{reactivateEnterpriseUserId && (
				<ReactivateEnterpriseUser
					onClose={() => setReactivateEnterpriseUserId(null)}
					userId={reactivateEnterpriseUserId}
				/>
			)}
		</>
	);
}
